/**
 * «این بازدیدکننده کجاست؟» — بدونِ پرسیدن، بدونِ اجازه‌ی موقعیت.
 *
 * فقط برای کاربرِ **بدونِ حسابِ وب**: تا وقتی کسی وارد نشده هیچ شهرِ دیفالتی
 * وجود نداره، و صفحه‌ی کشفی که به‌زور اتاوا نشون بده برای کسی که تورنتوئه
 * یعنی یه صفحه‌ی بی‌ربط. کاربرِ واردشده هیچ‌وقت این مسیر رو نمی‌بینه: شهرش
 * روی حسابش نوشته شده و همون معتبره.
 *
 * چرا IP و نه `navigator.geolocation`: اون یکی پنجره‌ی اجازه باز می‌کنه.
 * پرسیدنِ «موقعیتت رو بده» از کسی که تازه صفحه‌ی اول رو باز کرده، در ازای
 * چیزی که خودش با یه کلیک می‌تونه عوضش کنه، معامله‌ی بدیه.
 *
 * سرویس: ipwho.is — رایگان و بدونِ کلید و بدونِ ثبت‌نام، با CORS باز (یعنی
 * مستقیم از خودِ مرورگر صدا زده می‌شه و هیچ سرور یا تابعِ ابری وسط لازم
 * نیست). گزینه‌ی دیگه هدرهای جغرافیاییِ Cloudflare بود که سایت روشون
 * میزبانی می‌شه؛ دقیق‌تره ولی یه Pages Function می‌خواد که فقط بعد از
 * انتشار کار می‌کنه — یعنی چیزی که موقعِ توسعه و توی تست‌ها اصلاً اجرا
 * نمی‌شه و خرابیش هم دیده نمی‌شه.
 *
 * هر شکستی این‌جا بی‌صداست و `null` برمی‌گردونه: نتیجه‌ش برگشتن به شهرِ
 * پیش‌فرضِ فهرسته، نه خطایی روی صفحه. یه سرویسِ رایگانِ بیرونی همیشه می‌تونه
 * قطع باشه.
 *
 * خودِ core این فایل رو صدا نمی‌زنه — اپ از راهِ پورتِ `ipLocation` تزریقش
 * می‌کنه (نگاه کن به ports.ts و apps/web/src/boot.ts). برای همین تستِ E2E و
 * اپِ موبایل هیچ‌وقت به این سرویس نمی‌زنن.
 */
import { reportError } from './report.ts';
import type { IpLocation } from '../ports.ts';

export type { IpLocation };

const ENDPOINT = 'https://ipwho.is/';

/** بعد از این‌قدر، جواب هرچی هم باشه به دردِ اولین رندر نمی‌خوره. */
const TIMEOUT_MS = 4000;

const numOrNull = (v: unknown): number | null =>
  (typeof v === 'number' && Number.isFinite(v) ? v : null);

const textOrNull = (v: unknown): string | null =>
  (typeof v === 'string' && v.trim() ? v.trim() : null);

/**
 * جوابِ خامِ سرویس رو به شکلِ خودمون در می‌آره.
 *
 * جدا از خودِ `fetch` نوشته شده تا تست‌پذیر بمونه: چیزی که واقعاً می‌تونه
 * بشکنه همین‌جاست — سرویس هر وقت بخواد می‌تونه به‌جای عدد رشته بده، یا
 * فیلدی رو خالی بفرسته.
 */
export function parseIpLocation(raw: unknown): IpLocation | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  // ipwho.is خطا رو با HTTP 200 و `success: false` برمی‌گردونه.
  if (r.success === false) return null;
  const loc: IpLocation = {
    city: textOrNull(r.city),
    countryCode: textOrNull(r.country_code),
    lat: numOrNull(r.latitude),
    lng: numOrNull(r.longitude),
  };
  // نه اسمی، نه مختصاتی — یعنی هیچی. `null` صادق‌تر از یه آبجکتِ خالیه.
  return loc.city || (loc.lat !== null && loc.lng !== null) ? loc : null;
}

export async function lookupIpLocation(): Promise<IpLocation | null> {
  // `AbortSignal.timeout` روی هر مرورگرِ نه‌چندان قدیمی هست، ولی روی
  // محیط‌های تستِ node قدیمی‌تر نه — پس کنترلرِ دستی، که همه‌جا هست.
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(ENDPOINT, { signal: ctrl.signal });
    if (!res.ok) return null;
    return parseIpLocation(await res.json());
  } catch (e) {
    // قطعیِ شبکه و تایم‌اوت رایج‌ترین حالتن و خبر نیستن؛ ولی بی‌صدا رد شدن
    // یعنی اگه سرویس **همیشه** بشکنه هیچ‌وقت نمی‌فهمیم.
    reportError('ipCity.lookup', e);
    return null;
  } finally {
    clearTimeout(timer);
  }
}
